import React from 'react';
import './ReviewPrompt.css';

const ReviewPrompt = ({ 
  totalReviews, 
  averageRating, 
  hasReviewed, 
  onWriteReview, 
  onEditReview,
  onDeleteReview
}) => {
  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={`prompt-star ${i < rating ? 'filled' : ''}`}>
        ★
      </span>
    ));
  };

  // If user has already reviewed, show minimal UI with just edit/delete icons
  if (hasReviewed) {
    return (
      <div className="review-prompt-container review-prompt-minimal">
        <div className="review-prompt-minimal-wrapper">
          <div className="review-prompt-minimal-content">
            <div className="review-prompt-minimal-left">
              <div className="review-prompt-icon-small">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </div>
              <div className="review-prompt-minimal-text-wrapper">
                <span className="review-prompt-minimal-label">Your Recommendation</span>
                <span className="review-prompt-minimal-text">You've already recommended this professional</span>
              </div>
            </div>
            <div className="review-prompt-minimal-actions">
              <button 
                className="btn-review-action btn-edit-action"
                onClick={onEditReview}
                title="Edit your recommendation"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                </svg>
              </button>
              <button 
                className="btn-review-action btn-delete-action"
                onClick={onDeleteReview}
                title="Delete your recommendation"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3,6 5,6 21,6"></polyline>
                  <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show full prompt for users who haven't reviewed yet
  return (
    <div className="review-prompt-container">
      <div className="review-prompt-card">
        <div className="review-prompt-header">
          <div className="review-prompt-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
          </div>
          <div className="review-prompt-content">
            <h3 className="review-prompt-title">
              Help Build Their Reputation
            </h3>
            <p className="review-prompt-description">
              {totalReviews > 0
                ? `Join ${totalReviews} ${totalReviews === 1 ? 'other person' : 'others'} who have recommended this professional.`
                : 'Be the first to recommend this professional and help them stand out.'}
            </p>
          </div>
        </div>

        {totalReviews > 0 && averageRating > 0 && (
          <div className="review-prompt-stats">
            <div className="prompt-stat-item">
              <span className="prompt-stat-value">{averageRating.toFixed(1)}</span>
              <div className="prompt-stat-stars">{renderStars(Math.round(averageRating))}</div>
              <span className="prompt-stat-label">Average Rating</span>
            </div>
            <div className="prompt-stat-divider"></div>
            <div className="prompt-stat-item">
              <span className="prompt-stat-value">{totalReviews}</span>
              <span className="prompt-stat-label">{totalReviews === 1 ? 'Review' : 'Reviews'}</span>
            </div>
          </div>
        )}

        <div className="review-prompt-benefits">
          <div className="benefit-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            <span>Build professional credibility</span>
          </div>
          <div className="benefit-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            <span>Help them get discovered</span>
          </div>
          <div className="benefit-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            <span>Takes less than 2 minutes</span>
          </div>
        </div>

        <div className="review-prompt-actions">
          <button 
            className="btn-prompt-primary"
            onClick={onWriteReview}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
            Write a Recommendation
          </button>
        </div>

        <div className="review-prompt-footer">
          <p className="prompt-footer-text">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
            Your recommendation will be visible on their profile
          </p>
        </div>
      </div>
    </div>
  );
};

export default ReviewPrompt;
